import React, { useEffect, useState } from "react";
import { Row, Col, Dropdown, Spinner } from "react-bootstrap";
import { ThreeDots } from "react-bootstrap-icons";
import PageActions, { RefetchPages } from "./pageActions";
import { DeletePage2MutationFn } from "../../../graphql/projects.hooks";
import { Link } from "react-router-dom";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import {
  FolderDeleteMutationFn,
  FolderDuplicateMutationFn,
} from "../../../graphql/folder.hooks";
import { DomainProps, FolderProps, PageProps } from "./index";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { IMoveItems } from "../../../types";
import { ITEMS } from "../../../utils";
import { useReactiveVar } from "@apollo/client";
import { isHaveBeenSaved, isLoadingOfDeletion } from "../../../graphql/state";
import { CustomToast } from "../../../components/inputs/toast";
import { useTranslation } from "react-i18next";

interface FolderPageListProps {
  folderPageAccessType?: number;
  projectAccessType?: DomainProps;
  currentProjectAccessType?: DomainProps;
  folders?: FolderProps[];
  pages?: PageProps[];
  folderImgSrc: string;
  pageImgSrc: string;
  deletePage?: DeletePage2MutationFn;
  deleteFolder?: FolderDeleteMutationFn;
  duplicateFolder?: FolderDuplicateMutationFn;
  refetchPagesByDomain?: RefetchPages;
  domainId?: number | string;
  loading?: boolean;
  sharedValue?: string;
  selectedItems: IMoveItems;
  setSelectedItems: React.Dispatch<React.SetStateAction<IMoveItems>>;
}

const FolderPageListItemGrid: React.FC<{
  folderPageAccessType?: number;
  projectAccessType?: DomainProps;
  currentProjectAccessType?: DomainProps;
  item: PageProps | FolderProps;
  imgSrc: string;
  type: "folder" | "page";
  deletePage?: DeletePage2MutationFn;
  folderId?: number;
  domainId?: number | string;
  accessType?: number | string;
  sharedValue?: string;
  selectedItems: IMoveItems;
  setSelectedItems: React.Dispatch<React.SetStateAction<IMoveItems>>;
  checkboxBorder: string;
  wasChecked: number;
  setWasChecked: React.Dispatch<React.SetStateAction<number>>;
  refetchPagesByDomain?: RefetchPages;
}> = ({
  item,
  imgSrc,
  type,
  deletePage,
  folderId,
  domainId,
  projectAccessType,
  folderPageAccessType,
  sharedValue,
  selectedItems,
  setSelectedItems,
  wasChecked,
  setWasChecked,
  checkboxBorder,
  refetchPagesByDomain,
}) => {
  const lng = useGetUrlLocale();
  const link =
    type === ITEMS.FOLDER && folderId && domainId
      ? `${lng}/projects/${domainId}/folder/${folderId}`
      : `${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${item.id}`;
  const { data: User } = useUserQuery();
  const currentUser = User?.user?.email;

  const currentProjectAccessTypeIndex = projectAccessType?.find(
    (user) => user?.user?.email === currentUser
  );
  const currentProjectAccessType = currentProjectAccessTypeIndex?.accessType;

  const isDefaultImage =
    (item as PageProps)?.image?.length > 200 ||
    (item as PageProps)?.image === null;

  const changeCheckboxState = () => {
    if (selectedItems.folderIds.includes(item.id) && ITEMS.FOLDER === type) {
      setWasChecked(item.id);
      const updatedItems = selectedItems.folderIds.filter(
        (id) => id != item.id
      );
      setSelectedItems({
        pageIds: selectedItems.pageIds,
        folderIds: updatedItems,
      });
    } else if (
      !selectedItems.folderIds.includes(item.id) &&
      ITEMS.FOLDER === type
    ) {
      setWasChecked(null);
      setSelectedItems({
        pageIds: selectedItems.pageIds,
        folderIds: [...selectedItems.folderIds, item.id],
      });
    }

    if (selectedItems.pageIds.includes(item.id) && ITEMS.PAGE === type) {
      setWasChecked(item.id);
      const updatedItems = selectedItems.pageIds.filter((id) => id != item.id);
      setSelectedItems({
        folderIds: selectedItems.folderIds,
        pageIds: updatedItems,
      });
    } else if (
      !selectedItems.pageIds.includes(item.id) &&
      ITEMS.PAGE === type
    ) {
      setWasChecked(null);
      setSelectedItems({
        folderIds: selectedItems.folderIds,
        pageIds: [...selectedItems.pageIds, item.id],
      });
    }
  };

  return (
    <Col xl={3}>
      <div
        className={`${[...selectedItems.folderIds, ...selectedItems.pageIds].includes(item.id) ? "border-primary bg-primary-light" : "bg-white border-white"} border listItemGrid rounded-1 p-3 h-100 d-flex flex-column justify-content-between text-break position-relative`}
      >
        <input
          onClick={changeCheckboxState}
          className={`form-check-input position-absolute mt-0 ${
            !(ITEMS.FOLDER === type
              ? selectedItems.folderIds.includes(item.id)
              : selectedItems.pageIds.includes(item.id)) && checkboxBorder
          } ${wasChecked === item.id && "border-primary-subtle"}`}
          style={{
            left: `${type === ITEMS.FOLDER ? "15px" : "24px"}`,
            top: "24px",
          }}
          type="checkbox"
          checked={
            ITEMS.FOLDER === type
              ? selectedItems.folderIds.includes(item.id)
              : selectedItems.pageIds.includes(item.id)
          }
          value={item.id}
        />
        <Link
          to={link}
          state={sharedValue}
          target={type === "page" ? "_blank" : undefined}
        >
          <div
            className={`d-flex justify-content-center overflow-hidden ${isDefaultImage && "bg-additional-gray"} ${type === "page" ? "border rounded" : ""}`}
          >
            <div
              className={`text-reset page-prew ${isDefaultImage && "d-flex justify-content-center"}`}
            >
              <img
                src={
                  type === "page" && (item as PageProps).image
                    ? (item as PageProps).image
                    : imgSrc
                }
                className={`${isDefaultImage ? "h-100" : `img-fluid  ${(item as PageProps)?.type === 1 ? "" : "h-100"}`}`}
                alt={item.title}
              />
            </div>
          </div>
        </Link>
        <div className="d-flex align-items-center justify-content-between">
          <p className="fw-bold fs-5 mb-0">
            {item.title.length > 35
              ? `${item.title.slice(0, 35)}...`
              : item.title}
          </p>
          <Dropdown>
            <Dropdown.Toggle
              id="dropdown-basic"
              className={`bg-transparent border-0 ${[...selectedItems.folderIds, ...selectedItems.pageIds].includes(item.id) && "disabled-block"}`}
            >
              <ThreeDots />
            </Dropdown.Toggle>
            <PageActions
              type={type}
              deletePage={deletePage}
              pageId={item.id}
              itemId={item.id}
              domainId={domainId}
              accessType={currentProjectAccessType}
              refetchPagesByDomain={refetchPagesByDomain}
              folderPageAccessType={folderPageAccessType}
            />
          </Dropdown>
        </div>
      </div>
    </Col>
  );
};

const FolderPageListGrid: React.FC<FolderPageListProps> = ({
  folders,
  pages,
  folderImgSrc,
  pageImgSrc,
  deletePage,
  domainId,
  loading,
  projectAccessType,
  folderPageAccessType,
  selectedItems,
  setSelectedItems,
  refetchPagesByDomain,
}) => {
  const { t } = useTranslation();
  const isLoading = useReactiveVar(isLoadingOfDeletion);
  const haveBeenSaved = useReactiveVar(isHaveBeenSaved);

  const [wasChecked, setWasChecked] = useState<number | null>(null);
  const [checkboxBorder, setCheckboxBorder] = useState<string>("border");

  useEffect(() => {
    if (selectedItems.folderIds.length || selectedItems.pageIds.length) {
      setCheckboxBorder("border-dark");
    }
  }, [selectedItems]);

  return (
    <>
      {loading ? (
        <Spinner animation="border" role="status"></Spinner>
      ) : (
        <Row className={`g-4 ${isLoading && "pe-none opacity-75"}`}>
          {(folders || []).map((folder) => (
            <FolderPageListItemGrid
              key={folder.id}
              folderId={folder.id}
              item={folder}
              imgSrc={folderImgSrc}
              type="folder"
              domainId={domainId}
              projectAccessType={projectAccessType}
              folderPageAccessType={folderPageAccessType}
              refetchPagesByDomain={refetchPagesByDomain}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              checkboxBorder={checkboxBorder}
              wasChecked={wasChecked}
              setWasChecked={setWasChecked}
            />
          ))}
          {(pages || []).map((page) => (
            <FolderPageListItemGrid
              key={page.id}
              item={page}
              imgSrc={pageImgSrc}
              type="page"
              deletePage={deletePage}
              projectAccessType={projectAccessType}
              folderPageAccessType={folderPageAccessType}
              refetchPagesByDomain={refetchPagesByDomain}
              domainId={domainId}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              checkboxBorder={checkboxBorder}
              wasChecked={wasChecked}
              setWasChecked={setWasChecked}
            />
          ))}
        </Row>
      )}
      <CustomToast
        isShow={haveBeenSaved}
        setShow={isHaveBeenSaved}
        message={`${t("share.beenSaved")}`}
        isSeenClose={false}
        background="bg-success-light"
        text="text-success fw-400 fs-6"
        check={true}
      />
    </>
  );
};
export default FolderPageListGrid;
